import { create } from "zustand";
import { combine } from "zustand/middleware";

export type GripperTrainingMode = "binary" | "continuous";

export type RlRewardConfig = {
  successReward: number;
  dropoffReward: number;
  stepPenalty: number;
  smoothnessPenalty: number;
  // Shaping rewards (bounded, paid as Δ(% distance closed) each step).
  reachProgressReward: number; // max total per episode for EE→target progress
  transportProgressReward: number; // max total per episode for target→dropoff progress (after grasp)
};

export type RlTaskSpecThresholds = {
  minLiftHeight: number;
  maxEePickTargetDist: number;
  graspHoldSteps: number;
  placementHoldSteps: number;
  gripperClosedCtrlThreshold: number;
};

export type RlTaskSpecSettings = {
  pickTargetBodyUuid: string;
  dropTargetBodyUuid: string;
  // "gripper_target" or a body uuid. Used by both env export and warmstart collector.
  endEffectorRef: string;
  // If true, randomize end-effector position at reset in warm-start and include guidance in env export.
  randomizeEndEffectorLocationOnReset: boolean;
  // UI slider percent (0..100), mapped to a max randomization radius in meters by panel/export helpers.
  randomizeEndEffectorLocationVariationPct: number;
  // If true, exported env_config.json should instruct Python to randomize the pick-target freejoint pose at reset().
  randomizePickTargetLocationOnReset: boolean;
  // UI slider percent (0..100) for cube reset randomization radius (few inches -> full range).
  randomizePickTargetLocationVariationPct: number;
  // If true, exported env_config.json should instruct Python to randomize the drop-target freejoint pose at reset().
  // Note: Has no effect if dropTargetBodyUuid is not set (no drop-target exported).
  randomizeDropTargetLocationOnReset: boolean;
  thresholds: RlTaskSpecThresholds;
};

export type LastDownloadedProjectArchiveFingerprint = {
  computedAtIso: string;
  envId: string;
  modelName: string | null;
  projectArchiveSha256: string;
} | null;

const createInitialState = () => ({
  // Control-rate semantics (Gym step = one control step).
  controlHz: 30,
  // Episode horizon for time-limit truncation.
  horizonSeconds: 10,

  // Training-facing gripper action interface.
  gripperMode: "binary" as GripperTrainingMode,

  // Reward weights (mirrors warmstart collector reward terms).
  reward: {
    successReward: 10,
    dropoffReward: 10,
    stepPenalty: 0.01,
    smoothnessPenalty: 0.01,
    reachProgressReward: 4,
    transportProgressReward: 4,
  } satisfies RlRewardConfig,

  // Shared task-spec fields (project archive upload + warmstart dataset collection).
  taskSpec: {
    pickTargetBodyUuid: "",
    dropTargetBodyUuid: "",
    endEffectorRef: "gripper_target",
    randomizeEndEffectorLocationOnReset: false as boolean,
    randomizeEndEffectorLocationVariationPct: 50,
    randomizePickTargetLocationOnReset: false as boolean,
    randomizePickTargetLocationVariationPct: 100,
    randomizeDropTargetLocationOnReset: false as boolean,
    thresholds: {
      minLiftHeight: 0.05,
      maxEePickTargetDist: 0.12,
      graspHoldSteps: 8,
      placementHoldSteps: 12,
      gripperClosedCtrlThreshold: 127,
    } satisfies RlTaskSpecThresholds,
  } satisfies RlTaskSpecSettings,

  // For debugging compatibility mismatches across downloads.
  lastDownloadedProjectArchiveFingerprint: null as LastDownloadedProjectArchiveFingerprint,
});

export const useRlTrainingSettingsStore = create(
  combine(createInitialState(), (set, get) => ({
    actions: {
      setControlHz: (hz: number) => {
        const v = Math.max(1, Math.floor(Number(hz) || 1));
        set({ controlHz: v });
      },
      setHorizonSeconds: (sec: number) => {
        const v = Math.max(1, Number(sec) || 1);
        set({ horizonSeconds: v });
      },
      setGripperMode: (mode: GripperTrainingMode) => {
        set({ gripperMode: mode === "continuous" ? "continuous" : "binary" });
      },
      setReward: (partial: Partial<RlRewardConfig>) => {
        const prev = get().reward;
        const next: RlRewardConfig = {
          successReward: Number.isFinite(Number(partial.successReward))
            ? Number(partial.successReward)
            : prev.successReward,
          dropoffReward: Number.isFinite(Number(partial.dropoffReward))
            ? Number(partial.dropoffReward)
            : prev.dropoffReward,
          stepPenalty: Number.isFinite(Number(partial.stepPenalty))
            ? Number(partial.stepPenalty)
            : prev.stepPenalty,
          smoothnessPenalty: Number.isFinite(Number(partial.smoothnessPenalty))
            ? Number(partial.smoothnessPenalty)
            : prev.smoothnessPenalty,
          reachProgressReward: Number.isFinite(Number(partial.reachProgressReward))
            ? Number(partial.reachProgressReward)
            : prev.reachProgressReward,
          transportProgressReward: Number.isFinite(Number(partial.transportProgressReward))
            ? Number(partial.transportProgressReward)
            : prev.transportProgressReward,
        };
        set({ reward: next });
      },
      setTaskSpec: (partial: Partial<RlTaskSpecSettings>) => {
        const prev = get().taskSpec;
        set({
          taskSpec: {
            ...prev,
            ...partial,
            thresholds: partial.thresholds
              ? { ...prev.thresholds, ...partial.thresholds }
              : prev.thresholds,
          },
        });
      },
      setTaskSpecThresholds: (partial: Partial<RlTaskSpecThresholds>) => {
        const prev = get().taskSpec;
        set({
          taskSpec: {
            ...prev,
            thresholds: { ...prev.thresholds, ...partial },
          },
        });
      },
      setLastDownloadedProjectArchiveFingerprint: (fp: LastDownloadedProjectArchiveFingerprint) => {
        set({ lastDownloadedProjectArchiveFingerprint: fp });
      },
      reset: () => set(createInitialState()),
    },
  }))
);

// Selectors
export const useRlControlHz = () => useRlTrainingSettingsStore((s) => s.controlHz);
export const useRlHorizonSeconds = () => useRlTrainingSettingsStore((s) => s.horizonSeconds);
export const useRlGripperMode = () => useRlTrainingSettingsStore((s) => s.gripperMode);
export const useRlReward = () => useRlTrainingSettingsStore((s) => s.reward);
export const useRlTaskSpec = () => useRlTrainingSettingsStore((s) => s.taskSpec);
export const useRlTaskSpecPickTargetBodyUuid = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.pickTargetBodyUuid);
export const useRlTaskSpecDropTargetBodyUuid = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.dropTargetBodyUuid);
export const useRlTaskSpecEndEffectorRef = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.endEffectorRef);
export const useRlTaskSpecRandomizeEndEffectorLocationOnReset = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.randomizeEndEffectorLocationOnReset);
export const useRlTaskSpecRandomizeEndEffectorLocationVariationPct = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.randomizeEndEffectorLocationVariationPct);
export const useRlTaskSpecRandomizePickTargetLocationOnReset = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.randomizePickTargetLocationOnReset);
export const useRlTaskSpecRandomizePickTargetLocationVariationPct = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.randomizePickTargetLocationVariationPct);
export const useRlTaskSpecRandomizeDropTargetLocationOnReset = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.randomizeDropTargetLocationOnReset);
export const useRlTaskSpecThresholds = () =>
  useRlTrainingSettingsStore((s) => s.taskSpec.thresholds);
export const useRlLastDownloadedProjectArchiveFingerprint = () =>
  useRlTrainingSettingsStore((s) => s.lastDownloadedProjectArchiveFingerprint);
export const useRlTrainingSettingsActions = () => useRlTrainingSettingsStore((s) => s.actions);
